import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle,
  Error,
  CloudUpload,
  GetApp,
  Schedule,
  Storage,
  Speed,
  Language,
  Refresh,
  FileDownload,
  DriveFolderUpload
} from '@mui/icons-material';
import { getScrapingStatus, getDriveStatus, saveScrapedContentToDrive, listDriveFolders, getDriveUploadProgress } from '../api';

const ScrapingResults = ({ scrapingJobId, scrapingResult, onStartNew, selectedFolder: initialSelectedFolder }) => {
  const [status, setStatus] = useState(null);
  const [isDriveConnected, setIsDriveConnected] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [driveUploadResult, setDriveUploadResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [wpBatchSize, setWpBatchSize] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get latest status to ensure we have all stats
        const statusData = await getScrapingStatus(scrapingJobId);
        setStatus(statusData);
        
        // Check Drive status
        const driveStatus = await getDriveStatus();
        setIsDriveConnected(driveStatus.authenticated);
        
        if (driveStatus.authenticated) {
          const foldersResult = await listDriveFolders();
          setFolders(foldersResult.folders || []);
          
          // Automatically select the folder that was chosen during setup
          if (initialSelectedFolder) {
            setSelectedFolder(initialSelectedFolder);
          }
        }
      } catch (err) {
        console.error('Error fetching results data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (scrapingJobId) {
      fetchData();
    }
  }, [scrapingJobId, initialSelectedFolder]);

  const handleDownloadAll = () => {
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const downloadUrl = `${backendUrl}/download/job/${scrapingJobId}`;
    window.location.href = downloadUrl;
  };

  const handleDownloadWordPress = () => {
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const batchParam = wpBatchSize !== 'all' ? `?batch_size=${wpBatchSize}` : '';
    const downloadUrl = `${backendUrl}/download/wordpress/${scrapingJobId}${batchParam}`;
    window.location.href = downloadUrl;
  };

  const handleSaveToDrive = async () => {
    if (!selectedFolder) {
      alert('Please select a folder first');
      return;
    }
    
    setSavingToDrive(true);
    setUploadProgress({ current: 0, total: 0 });
    
    // Start real-time progress tracking
    const startProgressTracking = () => {
      // Set initial progress based on scraping results
      const estimatedTotal = result?.results?.length || 29;
      setUploadProgress({ current: 0, total: estimatedTotal, currentFile: null, status: 'uploading' });
      
      const progressInterval = setInterval(async () => {
        try {
          const progressData = await getDriveUploadProgress(scrapingJobId);
          const progress = progressData.progress;
          
          console.log('Progress update:', progress); // Debug log
          
          setUploadProgress({
            current: progress.completed,
            total: progress.total,
            currentFile: progress.current_file,
            status: progress.status
          });
          
          // Stop polling if upload is completed or failed
          if (progress.status === 'completed' || progress.status === 'failed') {
            clearInterval(progressInterval);
          }
        } catch (error) {
          console.warn('Failed to get progress:', error);
          // Don't clear interval on progress fetch error - upload might still be running
        }
      }, 1000); // Poll every second
      
      return progressInterval;
    };
    
    const progressInterval = startProgressTracking();
    
    try {
      const result = await saveScrapedContentToDrive(scrapingJobId, selectedFolder.id);
      
      // Clear progress tracking
      clearInterval(progressInterval);
      
      // Store the upload result to show in detailed results
      setDriveUploadResult(result);
      
    } catch (err) {
      // Clear progress tracking on error
      clearInterval(progressInterval);
      // Set error result
      setDriveUploadResult({
        success: false,
        error: err.message,
        uploaded_count: 0,
        failed_count: 0,
        total_files: 0,
        isTimeout: err.message.includes('timeout') || err.message.includes('Upload timeout')
      });
    } finally {
      setSavingToDrive(false);
      // Ensure progress interval is cleared
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatContentLength = (length) => {
    if (!length) return '0 chars';
    if (length >= 1000000) {
      return `${(length / 1000000).toFixed(1)}M chars`;
    } else if (length >= 1000) {
      return `${(length / 1000).toFixed(1)}K chars`;
    } else {
      return `${length} chars`;
    }
  };

  const getStatusIcon = (success) => {
    return success ? (
      <CheckCircle color="success" />
    ) : (
      <Error color="error" />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const result = status?.result || scrapingResult;
  const stats = {
    totalPages: result?.total_pages || 0,
    successful: result?.successful || 0,
    failed: result?.failed || 0,
    totalContentLength: result?.total_content_length || 0,
    driveFilesCount: result?.drive_files_count || 0,
    averageContentLength: result?.average_content_length || 0,
    duration: status?.duration_seconds || 0,
    startTime: status?.start_time,
    endTime: status?.end_time
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CheckCircle sx={{ color: 'white', fontSize: 32, mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
              Scraping Completed Successfully!
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
              Successfully scraped {stats.successful} of {stats.totalPages} pages
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Statistics Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Schedule color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Duration
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatDuration(stats.duration)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Language color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Pages Scraped
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.successful}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of {stats.totalPages} total
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Storage color="info" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Content Size
                </Typography>
                <Typography variant="h4" color="info.main">
                  {formatContentLength(stats.totalContentLength)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatContentLength(stats.averageContentLength)} avg
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Speed color="warning" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Speed
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.duration > 0 ? Math.round(stats.successful / stats.duration * 60) : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  pages/min
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Download Options
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<FileDownload />}
            onClick={handleDownloadAll}
            sx={{ minWidth: 200 }}
          >
            Download ZIP
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="wp-batch-size-label">Batch Size</InputLabel>
              <Select
                labelId="wp-batch-size-label"
                value={wpBatchSize}
                label="Batch Size"
                onChange={(e) => setWpBatchSize(e.target.value)}
              >
                <MenuItem value="all">All (single file)</MenuItem>
                <MenuItem value="25">25 posts/file</MenuItem>
                <MenuItem value="50">50 posts/file</MenuItem>
                <MenuItem value="100">100 posts/file</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<GetApp />}
              onClick={handleDownloadWordPress}
              sx={{ minWidth: 200 }}
              title="Download as WordPress WXR import file"
            >
              {wpBatchSize === 'all' ? 'Download for WordPress' : 'Download ZIP for WordPress'}
            </Button>
          </Box>
          
          {isDriveConnected && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<DriveFolderUpload />}
              onClick={handleSaveToDrive}
              disabled={savingToDrive || !selectedFolder}
              sx={{ minWidth: 200 }}
            >
              {savingToDrive ? 'Saving...' : 'Save to Drive'}
            </Button>
          )}
          
          <Button
            variant="outlined"
            size="large"
            startIcon={<Refresh />}
            onClick={onStartNew}
            sx={{ minWidth: 200 }}
          >
            Start New Job
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Download as ZIP for individual HTML files, or use "Download for WordPress" to get a WXR import file.
          {wpBatchSize !== 'all' && (
            <strong> Batched exports split posts into multiple smaller files to avoid WordPress import timeouts. Import each file separately in order.</strong>
          )}
          {isDriveConnected && ' You can also save to Google Drive for cloud storage.'}
        </Typography>
      </Paper>

      {/* Drive Folder Selection */}
      {isDriveConnected && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Google Drive Integration
          </Typography>
          
          {folders.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {folders.map((folder) => (
                <Chip
                  key={folder.id}
                  label={folder.name}
                  onClick={() => setSelectedFolder(folder)}
                  color={selectedFolder?.id === folder.id ? 'primary' : 'default'}
                  variant={selectedFolder?.id === folder.id ? 'filled' : 'outlined'}
                  icon={<CloudUpload />}
                />
              ))}
            </Box>
          ) : (
            <Alert severity="info">
              No Google Drive folders found. Please create a folder in your Drive first.
            </Alert>
          )}
          
          {stats.driveFilesCount > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {stats.driveFilesCount} files have already been saved to Google Drive during scraping.
            </Alert>
          )}
        </Paper>
      )}

      {/* Detailed Results */}
      {result?.results && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Detailed Results
          </Typography>
          
          {stats.failed > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {stats.failed} pages failed to scrape. Check the details below.
            </Alert>
          )}
          
          {/* Drive Upload Progress Notice */}
          {savingToDrive && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    Uploading files to Google Drive...
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {uploadProgress.total > 0 ? `${uploadProgress.current} of ${uploadProgress.total} files` : 'Starting upload...'}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant={uploadProgress.total > 0 ? "determinate" : "indeterminate"}
                    value={uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : undefined}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  {uploadProgress.currentFile && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Uploading: {uploadProgress.currentFile}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Alert>
          )}

          {/* Drive Upload Success Notice */}
          {driveUploadResult && driveUploadResult.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUpload />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {driveUploadResult.complete_success ? 
                      `Successfully uploaded all ${driveUploadResult.uploaded_count} files to Google Drive` :
                      `Uploaded ${driveUploadResult.uploaded_count} of ${driveUploadResult.total_files} files to Google Drive`
                    }
                  </Typography>
                  {driveUploadResult.failed_count > 0 && (
                    <Typography variant="caption" color="warning.main">
                      {driveUploadResult.failed_count} files failed to upload
                    </Typography>
                  )}
                </Box>
              </Box>
            </Alert>
          )}
          
          {/* Drive Upload Error Notice */}
          {driveUploadResult && !driveUploadResult.success && (
            <Alert 
              severity={driveUploadResult.isTimeout ? "warning" : "error"} 
              sx={{ mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Error />
                <Typography variant="body2">
                  {driveUploadResult.isTimeout ? (
                    <>
                      Upload timeout - files may still be uploading to Google Drive. 
                      Please check your Google Drive folder to verify if the files were uploaded successfully.
                    </>
                  ) : (
                    `Failed to upload files to Google Drive: ${driveUploadResult.error}`
                  )}
                </Typography>
              </Box>
            </Alert>
          )}
          
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            <List dense>
              {result.results.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(item.success)}
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body2" component="div">
                            <strong>{item.title || 'No title'}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.url}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          {item.success ? (
                            <>
                              {item.content_length && (
                                <Chip 
                                  label={formatContentLength(item.content_length)} 
                                  size="small" 
                                  variant="outlined"
                                />
                              )}
                              {item.drive_file_id && (
                                <Chip 
                                  icon={<CloudUpload />}
                                  label="Saved to Drive" 
                                  size="small" 
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                              {item.local_file_path && (
                                <Chip 
                                  icon={<GetApp />}
                                  label="Saved locally" 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                            </>
                          ) : (
                            <Chip 
                              label={item.error || 'Unknown error'} 
                              size="small" 
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < result.results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ScrapingResults;
